class CustomTabsComponent extends HTMLElement {
  constructor() {
    super();

    this.root = null;
    this.tabs = [];
    this.panels = [];
    this.activeIndex = 0;
    this.activationMode = 'auto';
    this.hoverActivation = false;
    this.fadeTimer = null;
    this.tabList = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.childController = null;
    this.morphObserver = null;
    this.rehydrateScheduled = false;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onPointerover = this.onPointerover.bind(this);
    this.onMutations = this.onMutations.bind(this);
  }

  connectedCallback() {
    // Host-level listeners persist across morph re-renders because the host
    // element itself survives. Bind them once on first connect.
    this.activationMode = this.dataset.activationMode === 'manual' ? 'manual' : 'auto';
    this.hoverActivation = this.dataset.hoverActivation === 'true' && window.matchMedia('(hover: hover)').matches;

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    if (this.hoverActivation) {
      this.addEventListener('pointerover', this.onPointerover);
    }

    this.#initialize();

    // The product-info section is morph()'d in place when a flavor variant is
    // selected (see custom-variant-picker.js). Morph copies attributes from
    // the server-rendered HTML onto our existing children, which re-applies
    // the `hidden` class to every tab button and clears the `hidden` attribute
    // from every panel — undoing the enhanced state we set up here.
    //
    // morph.js's own `updatedCallback` dispatch can't help us, because the
    // parsed-from-string newNode it passes to `instanceof Component` is just
    // an HTMLElement (custom elements aren't upgraded inside DOMParser docs).
    //
    // Detect that reset via a MutationObserver on the host and re-hydrate.
    this.morphObserver = new MutationObserver(this.onMutations);
    this.morphObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden'],
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('pointerover', this.onPointerover);
    this.morphObserver?.disconnect();
    this.morphObserver = null;
    this.childController?.abort();
    this.childController = null;
    this.resetPanelTransitionState();
  }

  onMutations() {
    if (this.rehydrateScheduled) return;
    this.rehydrateScheduled = true;
    queueMicrotask(() => {
      this.rehydrateScheduled = false;
      if (this.#looksLikeServerBaseline()) {
        this.#initialize();
      }
    });
  }

  /**
   * Returns true when the DOM under this host has been reset to its server-
   * rendered baseline (every tab carries the `hidden` class, no panel has the
   * `hidden` attribute) — which is exactly what morph leaves behind on a
   * flavor change.
   */
  #looksLikeServerBaseline() {
    const liveTabs = this.querySelectorAll('[data-custom-tab]');
    const livePanels = this.querySelectorAll('[data-custom-tab-panel]');
    if (liveTabs.length === 0 || livePanels.length === 0) return false;

    const allTabsHidden = Array.from(liveTabs).every((tab) => tab.classList.contains('hidden'));
    const noPanelHidden = Array.from(livePanels).every((panel) => !panel.hidden);
    return allTabsHidden && noPanelHidden;
  }

  #initialize() {
    this.root = this.closest('.custom-tabs');
    this.tabs = Array.from(this.querySelectorAll('[data-custom-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-custom-tab-panel]'));
    this.tabList = this.querySelector('.custom-tabs__tablist');
    this.prevBtn = this.querySelector('.custom-tabs__arrow--prev');
    this.nextBtn = this.querySelector('.custom-tabs__arrow--next');

    if (!this.root || this.tabs.length === 0 || this.panels.length === 0) {
      return;
    }

    this.root.dataset.enhanced = 'true';

    const defaultIndex = Number(this.dataset.defaultIndex);
    const baseIndex = Number.isFinite(defaultIndex) ? defaultIndex : 0;
    // Preserve the user's current tab across a flavor switch when possible;
    // fall back to the configured default on first connect.
    const desiredIndex = this.activeIndex || baseIndex;
    this.activeIndex = Math.max(0, Math.min(desiredIndex, this.tabs.length - 1));

    // Child-element listeners (arrow buttons, tablist scroll) are attached to
    // elements morph may swap out. Use a fresh AbortController each init so
    // old listeners are dropped cleanly and new ones bind to the live DOM.
    this.childController?.abort();
    this.childController = new AbortController();
    const { signal } = this.childController;

    this.syncTabMetaFromPanels();
    this.setActiveTab(this.activeIndex, { moveFocus: false, animate: false });

    if (this.tabList && this.prevBtn && this.nextBtn) {
      this.prevBtn.addEventListener(
        'click',
        () => {
          this.tabList.scrollBy({ left: -150, behavior: 'smooth' });
        },
        { signal }
      );

      this.nextBtn.addEventListener(
        'click',
        () => {
          this.tabList.scrollBy({ left: 150, behavior: 'smooth' });
        },
        { signal }
      );

      this.tabList.addEventListener('scroll', () => this.updateArrowState(), { signal });

      this.updateArrowState();
    }
  }

  updateArrowState() {
    if (!this.tabList || !this.prevBtn || !this.nextBtn) return;

    const scrollLeft = this.tabList.scrollLeft;
    const maxScrollLeft = this.tabList.scrollWidth - this.tabList.clientWidth;

    const tolerance = 2;

    if (scrollLeft <= tolerance) {
      this.prevBtn.disabled = true;
    } else {
      this.prevBtn.disabled = false;
    }

    if (scrollLeft >= maxScrollLeft - tolerance) {
      this.nextBtn.disabled = true;
    } else {
      this.nextBtn.disabled = false;
    }
  }

  onClick(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const button = target.closest('[data-custom-tab]');
    if (!(button instanceof HTMLButtonElement)) return;

    const nextIndex = this.tabs.indexOf(button);
    if (nextIndex < 0) return;

    this.setActiveTab(nextIndex, { moveFocus: false });
  }

  onPointerover(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const button = target.closest('[data-custom-tab]');
    if (!(button instanceof HTMLButtonElement)) return;

    const nextIndex = this.tabs.indexOf(button);
    if (nextIndex < 0 || nextIndex === this.activeIndex) return;

    this.setActiveTab(nextIndex, { moveFocus: false });
  }

  onKeydown(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement) || target.getAttribute('role') !== 'tab') {
      return;
    }

    const currentIndex = this.tabs.indexOf(target);
    if (currentIndex < 0) return;

    let nextIndex = null;
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        if (this.activationMode === 'manual') {
          event.preventDefault();
          this.setActiveTab(currentIndex, { moveFocus: false });
        }
        return;
      default:
        return;
    }

    if (nextIndex === null) return;

    event.preventDefault();
    this.tabs[nextIndex]?.focus();

    if (this.activationMode === 'auto') {
      this.setActiveTab(nextIndex, { moveFocus: false });
    }
  }

  syncTabMetaFromPanels() {
    this.tabs.forEach((tab, index) => {
      const panel = this.panels[index];
      if (!(panel instanceof HTMLElement)) return;

      // Tab labels are rendered server-side by the parent block reading
      // child_block.settings.tab_label. No client-side sync needed.

      const label = (panel.dataset.customTabLabel || '').trim();
      if (label) {
        const labelNode = tab.querySelector('.custom-tabs__tab-label');
        if (labelNode) {
          labelNode.textContent = label;
        } else {
          tab.textContent = label;
        }
      }
      tab.classList.remove('hidden');

      const iconUrl = (panel.dataset.customTabIconUrl || '').trim();
      if (!iconUrl) return;

      let iconNode = tab.querySelector('.custom-tabs__tab-icon');
      if (!(iconNode instanceof HTMLImageElement)) {
        iconNode = document.createElement('img');
        iconNode.className = 'custom-tabs__tab-icon';
        iconNode.alt = '';
        iconNode.setAttribute('aria-hidden', 'true');
        iconNode.loading = 'lazy';

        const labelNode = tab.querySelector('.custom-tabs__tab-label');
        tab.insertBefore(iconNode, labelNode || tab.firstChild);
      }
      iconNode.src = iconUrl;
    });
  }

  setActiveTab(index, { moveFocus = false, animate = true } = {}) {
    if (index < 0 || index >= this.tabs.length) {
      return;
    }

    const previousIndex = this.activeIndex;
    this.activeIndex = index;

    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;

      if (moveFocus && isActive) {
        tab.focus();
      }
    });

    this.panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === index;
      panel.hidden = !isActive;

      if ('inert' in panel) {
        panel.inert = !isActive;
      }
    });

    const shouldAnimate = animate && this.shouldAnimatePanels() && previousIndex !== index;
    if (!shouldAnimate) {
      this.resetPanelTransitionState();
      return;
    }

    const activePanel = this.panels[index];
    if (!(activePanel instanceof HTMLElement)) {
      return;
    }

    this.runPanelFadeIn(activePanel);
  }

  getTransitionEffect() {
    return this.root?.dataset.transitionEffect === 'hard' ? 'hard' : 'fade';
  }

  shouldAnimatePanels() {
    return this.getTransitionEffect() === 'fade' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  resetPanelTransitionState() {
    if (this.fadeTimer) {
      window.clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }

    this.panels.forEach((panel) => {
      panel.classList.remove('is-transitioning-in', 'is-transitioning-in-active');
    });
  }

  runPanelFadeIn(panel) {
    this.resetPanelTransitionState();

    panel.classList.add('is-transitioning-in');

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (panel.hidden) {
          return;
        }

        panel.classList.add('is-transitioning-in-active');
      });
    });

    this.fadeTimer = window.setTimeout(() => {
      panel.classList.remove('is-transitioning-in', 'is-transitioning-in-active');
      this.fadeTimer = null;
    }, 280);
  }
}

if (!customElements.get('custom-tabs-component')) {
  customElements.define('custom-tabs-component', CustomTabsComponent);
}
